import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useGetUsersByLookingForQuery } from "./slice/matchSlice";
import calculateAge from "../../../Common/commonfunctions";
import config from "../../../../config";
import axios from "axios";
import { toast } from "../../../Common/Toast";
import { useConnection } from "./ConnectionContext";
import { useNavigate, Link } from "react-router-dom"

const MatchCard = ({ user, data, showAll, handleConnect }) => {
  const { connections } = useConnection();
  const navigate = useNavigate();
  // Helper function to check if user is connected
  const isConnected = (userId, connectionRequest) => {
    return connections[userId] === 1 || connectionRequest === 1;
  };
  const viewProfile = ({match})=>{
    navigate(`/profile/${match?.profileId}`, {
      state: {
        profile: match,
        currentUser: user,
        source: "regular-matches"
      }
    });
  }
  
  return (
    <>
      {data.slice(0, showAll ? data.length : 2).map((match, i) => (
        <div className="match-row d-flex align-items-center mb-3" key={`${match.user_id}-${i}`}>        
          <img 
            src={match.profile_image ? `${config.baseURL}/uploads/profiles/${match.profile_image}` : "/images/no-image-found.webp"} 
            alt={`${match.first_name} ${match.last_name}`} 
            style={{ width: 60, height: 60, borderRadius: "50%" }} 
          />
          <div className="flex-grow-1 ms-3" style={{cursor:"pointer"}} onClick={()=>viewProfile({match})}>
            <span className="name-line fw-bold">{match.first_name} {match.last_name}</span>
            {match.isNew && <span className="new-pill ms-1 badge bg-success">New</span>}
            <br />   
            {calculateAge(match.birth_day, match.birth_month, match.birth_year)} years, {match.height}, {match.language}, {match.city}
            <br />
            {match.profession}
          </div>
          
          {isConnected(match.user_id, match.connectionRequest) ? 
          (<a className="btn-connect text-decoration-none text-center">
              <span className="small">Request Sent</span>
            </a>) 
           : (
            <a 
              href="#" 
              className="btn-connect text-decoration-none text-center" 
              onClick={(e) => {
                e.preventDefault();
                handleConnect(match.user_id, match.profileId);
              }}
            >
              <i className="fa fa-check-circle-o" aria-hidden="true"></i>
              <br />
              <span className="small">Add Now</span>
            </a>
          )}
        </div>
      ))}
    </>
  );
};

const MatchesSection = () => {
  const user = useSelector((state) => state.user.userInfo);
  const token = useSelector((state) => state.user.token);
  
  const searchFor = user?.looking_for === "Bride" ? "Groom" : "Bride";
  
  // Add refetch function from RTK Query
  const { data, isLoading, isError, refetch } = useGetUsersByLookingForQuery({
    id: user.id,
    looking_for: searchFor
  });
  
  const [premiumMatches, setPremiumMatches] = useState([]);
  const [newMatches, setNewMatches] = useState([]);
  const [showPremiumAll, setShowPremiumAll] = useState(false);
  const [showNewAll, setShowNewAll] = useState(false);
  const { updateConnection, connections } = useConnection();

  // Merge server data with connection context
  useEffect(() => {
    if (data?.success && Array.isArray(data.users)) {
      const updatedUsers = data.users.map((user) => ({
        ...user,
        // Use connection status from context if available
        connectionRequest: connections[user.user_id] || user.connectionRequest || 0,
        language: user.language || "English", 
        isNew: user.isNew || true,                        
        isCrown: user.isCrown || true,  
        isVip: user.isVip || true                     
      }));

      setPremiumMatches(updatedUsers);
      setNewMatches(updatedUsers);
    }
  }, [data, connections]); // Add connections to dependency array

  const handleConnect = async (userId, profileId) => {
    // Prevent duplicate requests
    const isAlreadyConnected = connections[userId] === 1;
    if (isAlreadyConnected) {
      toast.info("Already connected");
      return;
    }

    // Update UI immediately for both premium and new matches
    setPremiumMatches(prev => prev.map(match => 
      match.user_id === userId ? { ...match, connectionRequest: 1 } : match
    ));
    setNewMatches(prev => prev.map(match => 
      match.user_id === userId ? { ...match, connectionRequest: 1 } : match
    ));
    
    // Update connection context
    updateConnection(userId, 1);

    try {
      // Send the connection notification
      await axios.post(`${config.baseURL}/api/notifications/send`, {
        receiver_user_id: userId,
        receiver_profile_id: profileId,
        sender_user_id: user?.user_id,
        sender_profile_id: user?.profileId,
        type: "connect",
        message: `${user?.first_name} wants to connect with you`,
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      // Refetch data to ensure sync with server
      await refetch();
      
      toast.success("Connection request sent successfully!");

    } catch (error) {
      // Revert UI on error
      setPremiumMatches(prev => prev.map(match => 
        match.user_id === userId ? { ...match, connectionRequest: 0 } : match
      ));
      setNewMatches(prev => prev.map(match => 
        match.user_id === userId ? { ...match, connectionRequest: 0 } : match
      ));
      
      // Revert connection context
      updateConnection(userId, 0);
      
      // Error handling
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.message) {
        toast.error(error.message);
      } else {
        toast.error("Failed to send connection request");
      }
    }
  };

  if (isLoading) return <p>Loading matches...</p>;
  if (isError) return <p>Error loading matches.</p>;

  return (
    <div className="row g-4 mt-4">
      {/* Premium Matches */}
      <div className="col-lg-6">
        <div className="card-lite p-3">
          <h6>
            Premium Matches <span className="badge bg-danger ms-1">{premiumMatches?.length}</span>
          </h6>
          <div className="match-card" id="premiumMatches">
            <MatchCard 
              user={user}
              data={premiumMatches} 
              showAll={showPremiumAll} 
              handleConnect={handleConnect}
            />
            <span
              className="view-all-link cursor-pointer"
              onClick={() => setShowPremiumAll(!showPremiumAll)}
            >
              {showPremiumAll ? "View Less" : "View All"}
            </span>
          </div>
        </div>
      </div>

      {/* New Matches */}
      <div className="col-lg-6">
        <div className="card-lite p-3">
          <h6>
            New Matches <span className="badge bg-danger ms-1">{newMatches?.length}</span>
          </h6>
          <div className="match-card" id="newMatches">
            <MatchCard 
              data={newMatches} 
              showAll={showNewAll} 
              handleConnect={handleConnect} 
            />
            <span
              className="view-all-link cursor-pointer"
              onClick={() => setShowNewAll(!showNewAll)}
            >
              {showNewAll ? "View Less" : "View All"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MatchesSection;